import React, { CSSProperties, Dispatch, createContext, useContext, useReducer } from 'react';

type LayoutStateType = {
  fixedHeader?: JSX.Element | undefined;
  fixedHeaderHeight?: number;
  fixedFooter?: JSX.Element | undefined;
  fixedFooterHeight?: number;
  shouldShowFooter?: boolean;
  style: CSSProperties | undefined;
};

// 모든 액션들을 위한 타입
type LayoutActionType =
  | { type: 'SET_FIXED_HEADER'; elem: JSX.Element; fixedHeaderHeight?: number }
  | { type: 'SET_FIXED_BOTTOM'; elem: JSX.Element; fixedFooterHeight?: number; shouldShowFooter?: boolean }
  | { type: 'SET_STYLE'; style: CSSProperties }
  | { type: 'SET_STYLE_RESET' };

// 디스패치를 위한 타입 (Dispatch 를 리액트에서 불러올 수 있음), 액션들의 타입을 Dispatch 의 Generics로 설정
type LayoutDispatchType = Dispatch<LayoutActionType>;

// Context 만들기
const LayoutContext = createContext<LayoutStateType | null>(null);
const LayoutDispatchContext = createContext<LayoutDispatchType | null>(null);

// 리듀서
function reducer(state: LayoutStateType, action: LayoutActionType): LayoutStateType {
  switch (action.type) {
    case 'SET_FIXED_HEADER':
      return {
        ...state,
        fixedHeader: action.elem,
        fixedHeaderHeight: action.fixedHeaderHeight ?? 0,
      };
    case 'SET_FIXED_BOTTOM':
      return {
        ...state,
        fixedFooter: action.elem,
        fixedFooterHeight: action.fixedFooterHeight ?? 0,
        shouldShowFooter: action.shouldShowFooter,
      };
    case 'SET_STYLE':
      return { ...state, style: { ...action.style } };
    case 'SET_STYLE_RESET':
      return { ...state, style: {} };
    default:
      return { ...state };
  }
}

export function LayoutProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    fixedHeader: undefined,
    fixedFooter: undefined,
    shouldShowFooter: true,
    style: undefined,
  });

  return (
    <LayoutContext.Provider value={state}>
      <LayoutDispatchContext.Provider value={dispatch}>{children}</LayoutDispatchContext.Provider>
    </LayoutContext.Provider>
  );
}

// state 와 dispatch 를 쉽게 사용하기 위한 커스텀 Hooks
export function useLayoutState() {
  const state = useContext(LayoutContext);
  if (!state) throw new Error('Cannot find LayoutProvider'); // 유효하지 않을땐 에러를 발생
  return state;
}

export function useLayoutDispatch() {
  const dispatch = useContext(LayoutDispatchContext);
  if (!dispatch) throw new Error('Cannot find LayoutProvider'); // 유효하지 않을땐 에러를 발생
  return dispatch;
}
